import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { EASE } from "./Reveal";

export default function GalleryGrid({ items }) {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) =>
      setActive((a) => (a === null ? a : (a + dir + items.length) % items.length)),
    [items.length],
  );

  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, step]);

  if (!items.length) {
    return (
      <div
        className="border border-dashed border-line bg-surface p-16 text-center"
        data-testid="gallery-empty-state"
      >
        <p className="font-display text-2xl italic text-soften">
          Salon photos will appear here.
        </p>
      </div>
    );
  }

  const current = active !== null ? items[active] : null;

  return (
    <>
      <div className="columns-2 gap-4 md:columns-3 [&>*]:mb-4" data-testid="gallery-grid">
        {items.map((img, i) => (
          <button
            key={img.id}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`gallery-item-${img.id}`}
            className="group relative block w-full break-inside-avoid overflow-hidden border border-line bg-surface"
            aria-label={`Open image: ${img.alt}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              width="800"
              height={img.ratio === "landscape" ? "533" : "1000"}
              className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                img.ratio === "landscape" ? "aspect-[3/2]" : "aspect-[4/5]"
              }`}
            />
            <span className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/20" />
            <span className="absolute bottom-3 left-3 translate-y-2 bg-ivory/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
              {img.category}
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            role="dialog"
            aria-modal="true"
            aria-label={current.alt}
            onClick={close}
          >
            <button
              type="button"
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close image viewer"
              className="absolute right-5 top-5 flex h-[48px] w-[48px] items-center justify-center border border-white/20 text-ivory transition-colors hover:border-champagne hover:text-champagne"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 flex h-[48px] w-[48px] -translate-y-1/2 items-center justify-center border border-white/20 text-ivory transition-colors hover:border-champagne hover:text-champagne md:left-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              aria-label="Next image"
              className="absolute right-3 top-1/2 flex h-[48px] w-[48px] -translate-y-1/2 items-center justify-center border border-white/20 text-ivory transition-colors hover:border-champagne hover:text-champagne md:right-8"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <motion.figure
              key={current.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={current.src.replace("w=1000", "w=1600")}
                alt={current.alt}
                className="max-h-[78vh] w-auto max-w-full object-contain"
              />
              <figcaption className="mt-4 flex items-center justify-between text-xs text-ivory/60">
                <span>{current.alt}</span>
                <span className="font-display italic text-champagne">
                  {active + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
